import { Component, computed, inject, signal } from '@angular/core';
import { disabled, form, FormField, FormRoot, pattern, required } from '@angular/forms/signals';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { type GetIntegrationDto, type GetIntegrationStatusDto, IntegrationProvider, IntegrationsService, IntegrationStatus, type UpdateIntegrationDto } from '@api/uc-monitoring';
import { UcMonitoringStateStore } from '@locations';
import { ConfirmationDialogComponent, NotificationsService } from '@ui';
import { firstValueFrom } from 'rxjs';

interface ConnectionFormValue {
  appId: string;
  directoryId: string;
  secretValue: string;
}

@Component({
  imports: [FormField, FormRoot, MatFormFieldModule, MatInputModule],
  selector: 'avs-connections',
  styleUrl: './connections.component.scss',
  templateUrl: './connections.component.html',
})
export class ConnectionsComponent {
  protected readonly integration = signal<GetIntegrationDto | null | undefined>(undefined);
  protected readonly isIntegrationUnavailable = computed(() => !this.integration());
  protected readonly isServiceDisabled = computed(() => this.integration() === null);
  protected readonly isConnected = computed(() => Boolean(this.integration()?.enabled));
  protected readonly areInputsDisabled = computed(() => this.isIntegrationUnavailable() || this.isConnected());
  protected readonly permissionsAlertMessage = computed(() => {
    const integrationStatus = this.integrationStatus();
    const status = integrationStatus?.status ?? this.integration()?.status;

    if (!this.isConnected() || status !== IntegrationStatus.NOTENOUGHPERMISSIONS) {
      return null;
    }

    const missingPermissions = integrationStatus?.missingPermissions.join(', ');

    return missingPermissions
      ? `Permissions missing. Enable ${missingPermissions}`
      : integrationStatus?.reason ?? 'Permissions missing.';
  });
  protected readonly connectionModel = signal<ConnectionFormValue>({
    appId: '',
    directoryId: '',
    secretValue: '',
  });
  protected readonly connectionForm = form(this.connectionModel, (path) => {
    const credentialFields = [path.appId, path.directoryId, path.secretValue];

    for (const field of credentialFields) {
      required(field);
      pattern(field, /\S/);
    }

    disabled(path, () => this.areInputsDisabled());
  }, {
    submission: {
      action: () => this.submit(),
    },
  });
  protected readonly isConnectDisabled = computed(() => (
    this.isIntegrationUnavailable() ||
    this.connectionForm().invalid() ||
    this.connectionForm().submitting()
  ));
  protected readonly isDisconnecting = signal(false);
  private readonly dialog = inject(MatDialog);
  private readonly integrationStatus = signal<GetIntegrationStatusDto | null>(null);
  private readonly integrationsService = inject(IntegrationsService);
  private readonly ucMonitoring = inject(UcMonitoringStateStore);
  private readonly notificationService = inject(NotificationsService);
  private savedSecretValue = '';

  constructor() {
    this.loadIntegration();
  }

  protected async submit() {
    const integration = this.integration();

    if (!integration) {
      return;
    }

    await this.setIntegrationEnabled(
      integration,
      true,
      this.connectionModel(),
    );
  }

  protected async disconnect() {
    const integration = this.integration();

    if (!integration || this.isDisconnecting()) {
      return;
    }

    this.isDisconnecting.set(true);

    try {
      const confirmed = await firstValueFrom(
        ConfirmationDialogComponent.open(this.dialog, {
          cancelBtnText: 'Cancel',
          confirmBtnText: 'Disconnect',
          description: 'Are you sure you want to disconnect Microsoft Teams?',
          isDeleteTypePopup: true,
          title: 'Disconnect Microsoft Teams',
        }),
      );

      if (!confirmed) {
        return;
      }

      await this.setIntegrationEnabled(integration, false);
    } finally {
      this.isDisconnecting.set(false);
    }
  }

  private loadIntegration() {
    this.integrationsService.getIntegrations({})
      .subscribe({
        error: error => {
          const message = this.getErrorMessage(error);

          this.notificationService.showErrorMessage(`Failed to fetch integrations: ${message}`);
        },
        next: ({ data }) => {
          const integration = data?.find(({ provider }) => provider === IntegrationProvider['MS-TEAMS']) ?? null;

          this.setIntegrationState(integration);
        },
      });
  }

  private setIntegrationState(integration: GetIntegrationDto | null) {
    this.integration.set(integration);
    this.integrationStatus.set(null);

    if (integration) {
      this.savedSecretValue = integration.maskedSecret ?? '';
      this.connectionModel.set({
        appId: integration.externalAppId ?? '',
        directoryId: integration.externalTenantId ?? '',
        secretValue: integration.maskedSecret ?? '',
      });

      if (integration.enabled) {
        this.loadIntegrationStatus(integration.id);
      }
    }
  }

  private loadIntegrationStatus(id: string) {
    this.integrationsService.getIntegrationStatus({ id })
      .subscribe({
        error: error => {
          const message = this.getErrorMessage(error);

          this.notificationService.showErrorMessage(`Failed to fetch integration status: ${message}`);
        },
        next: integrationStatus => this.integrationStatus.set(integrationStatus),
      });
  }

  private async setIntegrationEnabled(
    integration: GetIntegrationDto,
    enabled: boolean,
    credentials?: ConnectionFormValue,
  ) {
    const payload: UpdateIntegrationDto = {
      enabled,
    };

    if (credentials) {
      payload.appId = credentials.appId;
      payload.directoryId = credentials.directoryId;

      if (credentials.secretValue !== this.savedSecretValue) {
        payload.secret = credentials.secretValue;
      }
    }

    try {
      await firstValueFrom(
        this.integrationsService.updateIntegration(
          {
            id: integration.id,
          },
          payload,
        ),
      );
      const updatedIntegration = {
        ...integration,
        enabled,
      };

      this.integration.set(updatedIntegration);
      this.ucMonitoring.reload();

      if (enabled) {
        this.loadIntegrationStatus(integration.id);
      } else {
        this.integrationStatus.set(null);
      }

      if (payload.secret !== undefined) {
        this.savedSecretValue = payload.secret;
      }
      this.notificationService.showSuccessMessage(
        enabled
          ? 'Microsoft Teams connection updated successfully.'
          : 'Microsoft Teams disconnected successfully.',
      );
    } catch (error) {
      const message = this.getErrorMessage(error);

      this.notificationService.showErrorMessage(
        `${enabled ? 'Failed to update integration' : 'Failed to disconnect Microsoft Teams'}: ${message}`,
      );
    }
  }

  private getErrorMessage(error: unknown) {
    return error instanceof Error ? error.message : 'Unknown error';
  }
}
